import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'
import type { LeagueSummary } from '@/features/league/types'
import styles from './MyPerformanceCard.module.css'

type MyPerformanceCardProps = {
  leagues: LeagueSummary[]
}

type CompetitionGroup = {
  competitionId:   string
  competitionName: string
  sport:           'FOOTBALL' | 'CS2'
  leagues:         LeagueSummary[]
}

const SPORT_DOT_COLOR: Record<string, string> = {
  FOOTBALL: 'var(--accent)',
  CS2:      '#FF6B35',
}

const FORMAT_LABEL: Record<string, string> = {
  OVERALL_POINTS: 'Geral',
  HEAD_TO_HEAD:   'H2H',
}

function groupByCompetition(leagues: LeagueSummary[]): CompetitionGroup[] {
  const map = new Map<string, CompetitionGroup>()
  for (const league of leagues) {
    const existing = map.get(league.competitionId)
    if (existing) {
      existing.leagues.push(league)
    } else {
      map.set(league.competitionId, {
        competitionId:   league.competitionId,
        competitionName: league.competitionName,
        sport:           league.competitionSport,
        leagues:         [league],
      })
    }
  }
  return Array.from(map.values())
}

function RankBadge({ rank }: { rank: number | null }) {
  if (rank === null) {
    return <div className={clsx(styles.rankBadge, styles.rankPre)}>—</div>
  }
  if (rank === 1) {
    return <div className={clsx(styles.rankBadge, styles.rank1)}>1º</div>
  }
  return <div className={clsx(styles.rankBadge, styles.rankN)}>{rank}º</div>
}

function PerformanceRow({ league }: { league: LeagueSummary }) {
  const navigate = useNavigate()
  const isPreComp = league.competitionStatus === 'NOT_STARTED' || league.rank === null
  const formatLabel = FORMAT_LABEL[league.format] ?? league.format

  function handleClick() {
    void navigate({
      to:     '/league/$leagueId',
      params: { leagueId: league.id },
      search: { tab: 'tabela', gw: undefined },
    })
  }

  return (
    <div className={styles.perfRow} onClick={handleClick} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') handleClick() }}>
      <RankBadge rank={league.rank} />
      <div className={styles.perfInfo}>
        <div className={styles.perfName}>{league.name}</div>
        <div className={styles.perfMeta}>
          <span>{league.memberCount} part.</span>
          <span className={styles.formatDot} />
          <span>{formatLabel}</span>
        </div>
      </div>
      <div className={styles.perfRight}>
        {isPreComp ? (
          <div className={styles.perfPreLabel}>Aguarda R1</div>
        ) : (
          <>
            <div className={styles.perfPts}>{league.totalPoints}</div>
            <div className={styles.perfPtsSub}>pts</div>
          </>
        )}
      </div>
    </div>
  )
}

export function MyPerformanceCard({ leagues }: MyPerformanceCardProps) {
  const navigate = useNavigate()
  const groups = groupByCompetition(leagues)

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>Meu Desempenho</span>
        <button
          type="button"
          className={styles.action}
          onClick={() => void navigate({ to: '/ligas' })}
        >
          Gerenciar ligas
        </button>
      </div>

      {groups.map((group, i) => (
        <div key={group.competitionId} className={styles.sportGroup}>
          <div className={clsx(styles.sportHeading, i === 0 && styles.sportHeadingFirst)}>
            <span
              className={styles.sportDot}
              style={{ background: SPORT_DOT_COLOR[group.sport] ?? 'var(--accent)' }}
            />
            {group.competitionName}
          </div>
          {group.leagues.map(league => (
            <PerformanceRow key={league.id} league={league} />
          ))}
        </div>
      ))}

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.footerLink}
          onClick={() => void navigate({ to: '/ligas' })}
        >
          Ver tabelas completas →
        </button>
      </div>
    </div>
  )
}
